import React, { useState } from "react";
import { Dialog } from "@headlessui/react";
import { CheckIcon, HeartIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import DonationPopup from "./DonationPopup";
import SubscriptionModal from "./SubscriptionModal";
import { bundles } from "./BundlesData";
import AnanasSolutionsLogo from "../../images/AnanasSolutionsLogo.webp";
import Button from "../../components/controls/Button";
import Badge from "../../components/controls/Badge";
import PageHeader from "../../components/controls/PageHeader";

const durations = [
  { value: 3, label: "Trimestriel (3 mois)" },
  { value: 6, label: "Semestriel (6 mois)" },
  { value: 12, label: "Annuel (1 an)" },
];

const BecomePartner = () => {
  const [isDonationOpen, setDonationOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(6);
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const handleSubscribe = (priceId) => {
    setSelectedPriceId(priceId);
    setShowModal(true);
  };

  return (
    <div className=" border-t border-papayaText/5 mb-8">
      <div className="w-full text-onWhite">
        <PageHeader
          title={t("becomePartner.pageTitle")}
          subtitle={t("becomePartner.pageDescription")}
          logoSrc={AnanasSolutionsLogo}
          rounded={false}
          align="center"
          fullBleed={true} // plein écran horizontal
          height="md" // sm | md | lg
          gradient={{
            from: "#E9FBFA",
            via: "#FFF9E6",
            to: "#FFE9EC",
          }}
          patternSize="32px 32px"
          patternOpacity={15}
        />

        <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 mb-8 items-center">
          {durations.map(({ value, label }) => {
            // Badge condition (e.g. only for 12 months)
            const isAnnual = value === 12;
            const badgeText = isAnnual ? "Économisez!" : null;

            return (
              <div
                key={value}
                className="relative flex flex-col items-center pt-6"
              >
                {badgeText && (
                  <div className="absolute  right-0 translate-x-1/4 -translate-y-1/2  z-10">
                    <Badge variant="new">{badgeText}</Badge>
                  </div>
                )}
                <Button
                  onClick={() => setSelectedDuration(value)}
                  color="primary"
                  size="md"
                  selected={selectedDuration === value}
                  className="px-4 py-2 min-w-[200px]"
                >
                  {label}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap justify-center gap-6 ">
          {Object.entries(bundles)
            .map(([bundleKey, bundle]) => ({ ...bundle, key: bundleKey }))
            .filter((bundle) =>
              bundle.pricing.some((p) => p.duration === selectedDuration)
            )
            .map((bundle) => {
              const pricingObj = bundle.pricing.find(
                (p) => p.duration === selectedDuration
              );
              const currentPrice = pricingObj?.finalPrice ?? 0;
              const originalPrice = pricingObj?.originalPrice;
              let rabaisText = null;

              if (selectedDuration === 12) {
                if (bundle.key === "impact") {
                  rabaisText = "5 %";
                } else if (bundle.key === "rayonnement") {
                  rabaisText = "10 %";
                }
              }

              const pricePerMonth = (currentPrice / selectedDuration).toFixed(
                2
              );
              const translatedName = t(
                `becomePartner.bundles.${bundle.key}.name`
              );
              const translatedDescription = t(
                `becomePartner.bundles.${bundle.key}.description`
              );
              const translatedOptions = bundle.options;

              return (
                <div
                  key={bundle.key}
                  className="relative border rounded-xl shadow-md flex flex-col items-center bg-white hover:shadow-lg transition cursor-pointer hover:scale-105 transform duration-300 border-gray-300 w-full sm:w-1/2 lg:w-[23%] max-w-sm"
                >
                  {rabaisText && (
                    <div className="absolute top-2 right-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-md font-semibold px-4 py-2 rounded-full shadow-lg">
                      -{rabaisText}
                    </div>
                  )}

                  <h3 className="text-2xl font-bold text-center my-6 pt-4">
                    {translatedName}
                  </h3>

                  <p className="text-sm text-gray-500 mb-1">
                    {t("becomePartner.fromPrice")}
                  </p>

                  <div className="flex items-center gap-2 mb-1">
                    {originalPrice && (
                      <span className="text-gray-400 line-through text-sm">
                        {(originalPrice / selectedDuration).toFixed(2)} $
                      </span>
                    )}
                    <p className="font-semibold text-xl text-[#3a3a3a]">
                      {pricePerMonth} $ / {t("calendar.month")}
                    </p>
                  </div>

                  <div className="text-xs text-gray-600 mb-2 italic text-center">
                    <div>
                      {t("becomePartner.fullPayment")}:{" "}
                      {originalPrice ? (
                        <>
                          <span className="line-through text-gray-400 mr-1">
                            {originalPrice.toFixed(2)} $
                          </span>
                          <span>{currentPrice.toFixed(2)} $</span>
                        </>
                      ) : (
                        <span>{currentPrice.toFixed(2)} $</span>
                      )}
                    </div>
                    {t("becomePartner.fullPaymentDescription")}
                  </div>

                  <Button
                    onClick={() => handleSubscribe(bundle.id)}
                    color="primary"
                    size="md"
                    className="px-4 py-2 mb-4"
                  >
                    {t("becomePartner.ctaButton")}
                  </Button>

                  <div className="w-full">
                    {translatedOptions.map((option, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-4 mb-4 mx-8"
                      >
                        <div className="flex-shrink-0">
                          <CheckIcon className="text-primary h-4 w-4 mt-1" />
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-600 text-left max-w-xs mx-auto">
                            {option}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
        <div>
          <button
            onClick={() => setDonationOpen(true)}
            className="w-1/6 bg-bgCyan text-white py-2 rounded-full text-sm font-medium hover:bg-hoverCyan transition mt-8 hidden"
          >
            Faire un don <HeartIcon className="h-4 w-4 inline-block ml-1" />
          </button>

          <SubscriptionModal
            selectedPriceId={selectedPriceId}
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            selectedDuration={selectedDuration}
          />

          <DonationPopup
            isOpen={isDonationOpen}
            onClose={() => setDonationOpen(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default BecomePartner;
